/**
 * ShipMissile.ts
 * Pooled heavy secondary projectile for the Courier Ship (World 2).
 *
 * Sibling to {@link ShipProjectile} — same lifecycle, same right-only
 * travel direction, same camera-relative cull rule. Differences:
 *   - Spawned by `tryFireShipMissile()` (hold Attack ≥ 0.5 s), not by
 *     the auto-fire laser path.
 *   - Slower, larger, longer-lived, and deals heavier damage (with a
 *     splash radius — owned by the collision passes, not by the entity).
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling. Always
 * acquire from the pool and call `spawn()` rather than `new`-ing directly
 * (the constructor exists only so `shipCombat.ts` can pre-warm the pool
 * at module load).
 *
 * NO React imports, NO Skia imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module ShipMissile
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import { VIEWPORT_WIDTH } from '../../constants/gameConstants';
import {
  SHIP_MISSILE_WIDTH,
  SHIP_MISSILE_HEIGHT,
  SHIP_MISSILE_SPEED,
  SHIP_MISSILE_TTL_S,
} from '../../constants/spaceConstants';

/**
 * Off-screen margin (px) past the right edge of the visible window
 * before a missile is considered despawned. Matches the laser margin
 * so the two pools cull at the same line.
 */
export const SHIP_MISSILE_OFFSCREEN_MARGIN = 32;

/**
 * ShipMissile — pooled heavy projectile fired by holding Attack.
 *
 * Lifecycle: acquire from pool → spawn(x, y) → update(dt) → dead?
 * release. Death is set by: TTL expiry, off-screen cull, or a
 * collision-pass writing `dead = true` after the missile detonates.
 */
export class ShipMissile extends BaseEntity {
  /** Monotonic ID counter for stable list keys across pool recycling. */
  private static nextId = 1;

  /** Re-issued on every spawn so renderers get a stable key per "use". */
  public spawnId = 0;

  /** True while this missile is actively travelling. */
  public active = false;

  /** Seconds since spawn — used for TTL despawn. */
  private lifeTime = 0;

  constructor() {
    super(0, 0, SHIP_MISSILE_WIDTH, SHIP_MISSILE_HEIGHT);
    this.dead = true; // starts dead until spawned
  }

  /**
   * Initialise the missile at a world position. Always travels right.
   *
   * @param x - Spawn X in world space (logical px).
   * @param y - Spawn Y in world space (logical px).
   */
  public spawn(x: number, y: number): void {
    this.spawnId = ShipMissile.nextId++;
    this.x = x;
    this.y = y;
    this.vx = SHIP_MISSILE_SPEED;
    this.vy = 0;
    this.dead = false;
    this.active = true;
    this.lifeTime = 0;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.dead = true;
    this.active = false;
    this.lifeTime = 0;
  }

  /**
   * Advance the missile by one fixed timestep.
   * Despawns on TTL expiry or after exiting the visible window.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused today; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.lifeTime += dt;
    if (this.lifeTime >= SHIP_MISSILE_TTL_S) {
      this.dead = true;
      this.active = false;
      return;
    }

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    const rightCullX =
      spaceState.cameraX + VIEWPORT_WIDTH + SHIP_MISSILE_OFFSCREEN_MARGIN;
    if (this.x + this.width > rightCullX) {
      this.dead = true;
      this.active = false;
    }
  }
}
